import { createFileRoute, Link } from "@tanstack/react-router";
import {
  ArrowRight,
  MapPin,
  Phone,
  Search,
  Globe,
  Map,
  Target,
  Bot,
  Wrench,
  Droplet,
  Home,
  Zap,
  TreePine,
  Building2,
  Stethoscope,
  Scale,
} from "lucide-react";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { SiteLayout } from "@/components/SiteLayout";
import { SpringsOfficeMap } from "@/components/SpringsOfficeMap";
import { PageHero } from "@/components/PageHero";
import heroSprings from "@/assets/towns/colorado-springs.jpg";
import { Breadcrumbs } from "@/components/Breadcrumbs";
import {
  SCHEMA_LOCALBUSINESS_ID,
  SCHEMA_ORG_ID,
  SCHEMA_PERSON_ID,
  SITE_URL,
} from "@/lib/entitySchema";
import {
  CS_NEIGHBORHOODS,
  CS_ZIPS,
  buildCSAreaServed,
} from "@/lib/coloradoSpringsCoverage";
import { CLIENT_REVIEWS, aggregateRatingFromReviews } from "@/lib/clientReviews";
import { SERVICE_PRICING } from "@/lib/pricingData";

const SLUG = "colorado-springs";
const CANONICAL = `${SITE_URL}/${SLUG}`;

const NEIGHBORHOODS = CS_NEIGHBORHOODS;


const SERVICE_SPOKES = [
  { to: "/colorado-springs-seo", title: "Colorado Springs SEO & Web Design Agency — CWI", icon: Search, body: "Map Pack rankings and on-page SEO for Springs trades, healthcare, and pro-services." },
  { to: "/colorado-springs-web-design", title: "Colorado Springs Web Design", icon: Globe, body: "Fast, mobile-first websites built around how Springs customers actually buy." },
  { to: "/colorado-springs-google-maps", title: "Colorado Springs Google Maps", icon: Map, body: "Top-3 Map Pack visibility for Briargate, Falcon, Monument, and Black Forest." },
  { to: "/colorado-springs-ppc", title: "Colorado Springs PPC", icon: Target, body: "Profitable Google Ads tuned to your service area, season, and capacity." },
  { to: "/colorado-springs-geo-ai", title: "Colorado Springs GEO AI", icon: Bot, body: "Get cited when locals ask ChatGPT, Gemini, or Perplexity for a Springs business." },
] as const;

const INDUSTRIES = [
  { to: "/hvac-marketing-colorado", label: "HVAC marketing in Colorado Springs", icon: Wrench },
  { to: "/plumbing-marketing-colorado", label: "Plumbing SEO in Colorado Springs", icon: Droplet },
  { to: "/roofing-seo-colorado", label: "Roofing marketing in the Springs", icon: Home },
  { to: "/electrical-contractor-marketing-colorado", label: "Electrical contractor marketing — Colorado Springs", icon: Zap },
  { to: "/landscaping-seo-colorado", label: "Landscaping SEO across the Pikes Peak region", icon: TreePine },
  { to: "/contractor-web-design-colorado", label: "Contractor web design in Colorado Springs", icon: Building2 },
  { to: "/dental-marketing-colorado", label: "Dental marketing for Colorado Springs practices", icon: Stethoscope },
  { to: "/law-firm-seo-colorado", label: "Law firm SEO — Colorado Springs", icon: Scale },
] as const;

const FAQS = [
  {
    q: "Are you actually based in Colorado Springs?",
    a: "Yes — our office is at 3210 E Woodmen Rd STE 110, Colorado Springs, CO 80920. You can stop by, meet Chris, and see the team. We're not a national brand pretending to be local — we've been a Colorado Springs digital marketing agency since 2006.",
  },
  {
    q: "What does a Colorado Springs marketing agency typically charge?",
    a: "Our Growth Marketing package — SEO, Google Maps, GEO AI, and social — is $1,650/month and is the most common engagement for Springs businesses. Websites start at $1,850 one-time, owned outright. We publish prices publicly because Springs owners deserve a straight answer, not a sales pitch.",
  },
  {
    q: "How long until I rank in the Colorado Springs Map Pack?",
    a: "Most Springs categories see meaningful Map Pack movement in 60–90 days. Competitive verticals like HVAC and roofing during peak season can take 4–6 months of consistent review velocity and content work. We set realistic timelines on day one, not vanity promises.",
  },
  {
    q: "Do you only work with Colorado Springs businesses?",
    a: "We're headquartered in Colorado Springs but serve clients across all of Colorado — Denver, Fort Collins, Boulder, Pueblo, Grand Junction, Aurora, and Greeley included. The Springs is our home market, but every Colorado business gets the same owner-led attention.",
  },
  {
    q: "Will I work directly with the owner?",
    a: "Yes — every client works directly with Chris Heidlebaugh. No account managers, no offshore teams, no junior staff handoffs. You get Chris's direct line and an average response time under one hour during business hours.",
  },
] as const;

const SCHEMAS = {
  "@context": "https://schema.org",
  "@graph": [
    {
      "@type": "LocalBusiness",
      "@id": `${CANONICAL}#localbusiness`,
      name: "Colorado Web Impressions — Colorado Springs Digital Marketing Agency",
      url: CANONICAL,
      mainEntityOfPage: CANONICAL,
      parentOrganization: { "@id": SCHEMA_ORG_ID },
      sameAs: [SITE_URL, `${SITE_URL}/`],
      telephone: "+17194193935",
      priceRange: "$$",
      areaServed: buildCSAreaServed(),
      address: {
        "@type": "PostalAddress",
        streetAddress: "3210 E Woodmen Rd STE 110",
        addressLocality: "Colorado Springs",
        addressRegion: "CO",
        postalCode: "80920",
        addressCountry: "US",
      },
      geo: {
        "@type": "GeoCoordinates",
        latitude: 38.9359976,
        longitude: -104.7653118,
      },
      founder: { "@id": SCHEMA_PERSON_ID },
      foundingDate: "2006",
      knowsAbout: [
        "SEO",
        "Web Design",
        "Google Maps Optimization",
        "PPC Advertising",
        "Generative Engine Optimization",
        "Local SEO",
        "Digital Marketing",
      ],
    },
    {
      "@type": "BreadcrumbList",
      "@id": `${CANONICAL}#breadcrumbs`,
      itemListElement: [
        { "@type": "ListItem", position: 1, name: "Home", item: SITE_URL },
        { "@type": "ListItem", position: 2, name: "Locations", item: `${SITE_URL}/service-areas` },
        { "@type": "ListItem", position: 3, name: "Colorado Springs", item: CANONICAL },
      ],
    },
    {
      "@type": "FAQPage",
      "@id": `${CANONICAL}#faq`,
      mainEntity: FAQS.map((f) => ({
        "@type": "Question",
        name: f.q,
        acceptedAnswer: { "@type": "Answer", text: f.a },
      })),
    },
    {
      "@type": "Service",
      "@id": `${CANONICAL}#service-catalog`,
      name: "Colorado Springs Digital Marketing Services",
      provider: { "@id": SCHEMA_LOCALBUSINESS_ID },
      areaServed: { "@type": "City", name: "Colorado Springs" },
      hasOfferCatalog: {
        "@type": "OfferCatalog",
        name: "Colorado Springs Marketing Packages",
        itemListElement: SERVICE_PRICING.map((s) => ({
          "@type": "Offer",
          itemOffered: { "@type": "Service", name: s.service },
          url: `${SITE_URL}/${s.slug}`,
          priceCurrency: "USD",
          price: (s.startingAt ?? "").replace(/[^0-9.]/g, "") || undefined,
        })),
      },
    },
    {
      "@type": "AggregateRating",
      "@id": `${CANONICAL}#rating`,
      itemReviewed: { "@id": SCHEMA_LOCALBUSINESS_ID },
      ...aggregateRatingFromReviews(CLIENT_REVIEWS),
    },
    ...CLIENT_REVIEWS.slice(0, 5).map((r, i) => ({
      "@type": "Review",
      "@id": `${CANONICAL}#review-${i + 1}`,
      itemReviewed: { "@id": SCHEMA_LOCALBUSINESS_ID },
      author: { "@type": "Person", name: r.author },
      datePublished: r.datePublished,
      reviewRating: { "@type": "Rating", ratingValue: r.rating, bestRating: 5 },
      reviewBody: r.body,
    })),
    {
      "@type": "WebPage",
      "@id": `${CANONICAL}#webpage`,
      url: CANONICAL,
      name: "Colorado Springs Digital Marketing Agency | Colorado Web Impressions",
      isPartOf: { "@id": `${SITE_URL}/#website` },
      about: { "@id": SCHEMA_LOCALBUSINESS_ID },
      speakable: {
        "@type": "SpeakableSpecification",
        cssSelector: [".speakable-intro", ".faq-answer", ".definition-box"],
      },
    },
  ],
};

export const Route = createFileRoute("/colorado-springs")({
  head: () => ({
    meta: [
        { name: "description", content: "Owner-led Colorado Springs SEO and web design since 2006. Local team, sub-2s mobile sites, and rankings that ring the phone. Free strategy call." },
      { title: "Colorado Springs Digital Marketing Agency | Colorado Web Impressions" },
    ],
    links: [{ rel: "canonical", href: CANONICAL }],
    scripts: [{ type: "application/ld+json", children: JSON.stringify(SCHEMAS) }],
  }),
  component: Page,
});

function Page() {
  return (
    <SiteLayout>
      <PageHero
        eyebrow="Colorado Springs Digital Marketing Agency"
        title={
          <>
            Colorado Springs'{" "}
            <span className="text-brand-gold">#1 Owner-Led</span>{" "}
            Digital Marketing Agency
          </>
        }
        description="SEO, web design, Google Maps, PPC, and GEO AI — built and managed by Chris Heidlebaugh from our Woodmen Road office. We've been ranking Colorado Springs businesses since 2006."
        image={{ src: heroSprings, alt: "Downtown Colorado Springs skyline with Pikes Peak at golden hour" }}
      >
        <div className="mt-6 flex justify-center">
          <Breadcrumbs
            items={[
              { label: "Home", to: "/" },
              { label: "Locations", to: "/service-areas" },
              { label: "Colorado Springs" },
            ]}
          />
        </div>
      </PageHero>

      {/* Intro */}
      <section className="bg-background py-14 lg:py-16">
        <div className="mx-auto max-w-3xl px-5 lg:px-8">
          <p className="speakable-intro text-lg leading-relaxed text-foreground/85">
            Colorado Springs is our home market — and our headquarters. We've
            been ranking Springs HVAC, plumbing, roofing, dental, and law-firm
            clients in the Map Pack for almost 20 years. We also serve
            businesses across Castle Rock, Monument, Parker, Fountain, Pueblo,
            and the broader Front Range — most engagements start in the
            Springs and expand into surrounding markets as rankings compound.
            When you call{" "}
            <a href="tel:+17194193935" className="font-bold text-brand-gold-deep hover:underline" aria-label="Call (719) 419-3935">
              (719) 419-3935
            </a>
            , Chris answers — not an account manager.
          </p>

          <div className="definition-box mt-8 rounded-2xl border border-brand-gold/30 bg-brand-gold/5 p-6">
            <div className="text-xs font-black uppercase tracking-[0.2em] text-brand-gold-deep">
              What is a Colorado Springs digital marketing agency?
            </div>
            <p className="mt-2 text-base leading-relaxed text-foreground/85">
              A Colorado Springs digital marketing agency is a local team that
              helps Springs-area businesses get found online — through SEO,
              Google Maps optimization, web design, paid ads, and AI search
              visibility. The best ones, like Colorado Web Impressions, are
              owner-led, transparently priced, and physically based in the
              Pikes Peak region.
            </p>
          </div>
        </div>
      </section>

      {/* Service spokes */}
      <section className="border-y border-border bg-section-alt py-16 lg:py-20">
        <div className="mx-auto max-w-6xl px-5 lg:px-8">
          <div className="text-center">
            <h2 className="text-3xl font-extrabold text-foreground sm:text-4xl">
              Our Colorado Springs services
            </h2>
            <p className="mx-auto mt-3 max-w-2xl text-lg text-muted-foreground">
              Every service we offer is built and tuned for the Colorado Springs market.
            </p>
          </div>
          <div className="mt-10 grid gap-6 md:grid-cols-2 lg:grid-cols-3">
            {SERVICE_SPOKES.map(({ to, title, icon: Icon, body }) => (
              <Link
                key={to}
                to={to}
                className="group rounded-2xl border border-border bg-card p-6 shadow-card transition-smooth hover:-translate-y-0.5 hover:border-brand-gold/60 hover:shadow-card-hover"
              >
                <div className="grid h-11 w-11 place-items-center rounded-lg bg-brand-gold/10 text-brand-gold-deep">
                  <Icon className="h-5 w-5" />
                </div>
                <h3 className="mt-4 text-lg font-extrabold text-foreground group-hover:text-brand-gold-deep">
                  {title}
                </h3>
                <p className="mt-2 text-sm leading-relaxed text-muted-foreground">{body}</p>
                <span className="mt-4 inline-flex items-center gap-1 text-sm font-bold text-brand-gold-deep">
                  Learn more <ArrowRight className="h-4 w-4" />
                </span>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* Industries */}
      <section className="bg-background py-16 lg:py-20">
        <div className="mx-auto max-w-5xl px-5 lg:px-8">
          <h2 className="text-3xl font-extrabold text-foreground sm:text-4xl">
            Industries we serve in the Springs
          </h2>
          <p className="mt-3 text-lg text-muted-foreground">
            We specialize in the Colorado Springs verticals that benefit most
            from local-first marketing.
          </p>
          <ul className="mt-8 grid gap-3 sm:grid-cols-2">
            {INDUSTRIES.map(({ to, label, icon: Icon }) => (
              <li key={to}>
                <Link
                  to={to}
                  className="flex items-center gap-3 rounded-xl border border-border bg-card p-4 shadow-card transition-smooth hover:-translate-y-0.5 hover:border-brand-gold/60"
                >
                  <Icon className="h-5 w-5 shrink-0 text-brand-gold-deep" />
                  <span className="text-sm font-bold text-foreground">{label}</span>
                  <ArrowRight className="ml-auto h-4 w-4 text-brand-gold-deep" />
                </Link>
              </li>
            ))}
          </ul>
        </div>
      </section>

      {/* Neighborhoods + service-areas link */}
      <section className="border-t border-border bg-section-alt py-14">
        <div className="mx-auto max-w-5xl px-5 lg:px-8">
          <h2 className="text-2xl font-extrabold text-foreground">
            Colorado Springs neighborhoods we serve
          </h2>
          <p className="mt-2 text-sm text-muted-foreground">
            We build neighborhood-aware content because Briargate, Black Forest,
            and Old Colorado City each have different buyer behavior.
          </p>
          <ul className="mt-6 flex flex-wrap gap-2">
            {NEIGHBORHOODS.map((n) => (
              <li
                key={n}
                className="inline-flex items-center gap-1.5 rounded-full border border-border bg-card px-3.5 py-1.5 text-xs font-bold text-foreground"
              >
                <MapPin className="h-3 w-3 text-brand-gold-deep" />
                {n}
              </li>
            ))}
          </ul>
          <Link
            to="/service-areas"
            className="mt-6 inline-flex items-center gap-1.5 text-sm font-bold text-brand-gold-deep hover:underline"
          >
            View all Colorado service areas <ArrowRight className="h-4 w-4" />
          </Link>

          <div className="mt-12">
            <h3 className="text-xl font-extrabold text-foreground">
              Colorado Springs ZIP codes we serve
            </h3>
            <p className="mt-2 text-sm text-muted-foreground">
              Where our Springs clients win calls and leads — from the
              northern reaches of Monument and Black Forest down through the
              base communities and Fountain Valley.
            </p>
            <ul className="mt-5 flex flex-wrap gap-1.5">
              {CS_ZIPS.map((zip) => (
                <li
                  key={zip}
                  className="inline-flex items-center rounded-md border border-border bg-card px-2.5 py-1 font-mono text-xs font-bold text-foreground"
                >
                  {zip}
                </li>
              ))}
            </ul>
          </div>
        </div>
      </section>


      {/* FAQ */}
      <section className="bg-background py-16 lg:py-20">
        <div className="mx-auto max-w-3xl px-5 lg:px-8">
          <h2 className="text-3xl font-extrabold text-foreground sm:text-4xl">
            Colorado Springs FAQ
          </h2>
          <Accordion
            type="single"
            collapsible
            className="mt-8 divide-y divide-border rounded-2xl border border-border bg-card shadow-card"
          >
            {FAQS.map((f, i) => (
              <AccordionItem key={f.q} value={`faq-${i}`} className="border-0 px-5 sm:px-6">
                <AccordionTrigger className="py-5 text-left text-base font-bold text-foreground hover:no-underline">
                  {f.q}
                </AccordionTrigger>
                <AccordionContent className="faq-answer pb-5 pt-0 text-base leading-relaxed text-foreground/85">
                  {f.a}
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      </section>

      {/* Why owners switch */}
      <section className="py-20">
        <div className="mx-auto max-w-6xl px-5 lg:px-8">
          <div className="mx-auto max-w-2xl text-center">
            <h2 className="text-3xl font-extrabold text-foreground sm:text-4xl">
              Why Colorado Springs owners switch to us
            </h2>
            <p className="mt-4 text-lg text-muted-foreground">
              It's not always about being burned. Most owners we talk to are just tired of being pitched, handed off, overcharged, and ignored.
            </p>
          </div>
          <ul className="mt-10 grid gap-5 sm:grid-cols-2 lg:grid-cols-4">
            {[
              {
                pain: "Pitched, then ghosted",
                answer: "You work with the owner — no sales reps, no handoffs after you sign.",
              },
              {
                pain: "Account manager #4 this year",
                answer: "Same Colorado-based person start to finish. One point of contact, period.",
              },
              {
                pain: "$5K/month, zero clarity",
                answer: "Flat, published pricing — full Lead Generation is $1,650/mo. Cancel anytime.",
              },
              {
                pain: "Just another invoice",
                answer: "Capped roster, Colorado Springs based. We pick up the phone — usually same day.",
              },
            ].map((item) => (
              <li
                key={item.pain}
                className="rounded-2xl border border-border bg-card p-6 shadow-card"
              >
                <div className="text-xs font-bold uppercase tracking-[0.2em] text-muted-foreground">
                  What you're used to
                </div>
                <div className="mt-2 text-base font-extrabold text-foreground">
                  "{item.pain}"
                </div>
                <div className="mt-4 text-xs font-bold uppercase tracking-[0.2em] text-brand-gold-deep">
                  With us
                </div>
                <p className="mt-2 text-sm leading-relaxed text-foreground/85">
                  {item.answer}
                </p>
              </li>
            ))}
          </ul>
        </div>
      </section>

      <SpringsOfficeMap />

      {/* CTA */}
      <section className="bg-section-alt py-20">
        <div className="mx-auto max-w-3xl px-5 text-center lg:px-8">
          <h2 className="text-3xl font-extrabold text-foreground sm:text-4xl">
            Ready to talk to a real Colorado Springs marketing team?
          </h2>
          <p className="mt-4 text-lg text-muted-foreground">
            Moving to or already in the Pikes Peak region? Start with our{" "}
            <Link
              to="/moving-to-colorado-springs"
              className="font-semibold text-brand-gold-deep underline-offset-2 hover:underline"
            >
              local's guide to Colorado Springs
            </Link>
            , or get a free proposal from Chris directly. No sales pitch,
            no pressure — straight answers and a real plan.
          </p>
          <div className="mt-8 flex flex-wrap items-center justify-center gap-4">
            <Link
              to="/contact"
              className="inline-flex items-center gap-2 rounded-md bg-gradient-gold px-6 py-3 text-base font-bold text-brand-black shadow-gold transition-spring hover:-translate-y-0.5"
            >
              Get a free proposal in Colorado Springs
              <ArrowRight className="h-4 w-4" />
            </Link>
            <a
              href="tel:+17194193935"
              className="inline-flex items-center gap-2 rounded-md border-2 border-foreground/20 bg-card px-6 py-3 text-base font-bold text-foreground transition-smooth hover:border-brand-gold/60 hover:text-brand-gold-deep"
             aria-label="Call (719) 419-3935">
              <Phone className="h-4 w-4" />
              (719) 419-3935
            </a>
          </div>
        </div>
      </section>
    </SiteLayout>
  );
}
